import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import jwtDecode from 'jwt-decode';
import type { JwtPayload } from 'jwt-decode';
import { isBoolean } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import type {
  DepositContext,
  DepositResult,
  XPay3DS2Info
} from '../../../../../../../../types/Cashier';
import { sanitizeDeposit } from '../../../../../../../../utils/cashier';
import { DDCStatus } from '../../RejectedX3dSecure2RequiredError';
import DDCStatusMessages from '../DDCStatusMessages/DDCStatusMessages';

interface XPAYV3JwtPayload extends JwtPayload {
  payload: {
    requesttypedescriptions: string[];
  };
}

type Props = DepositResult & DepositContext;

const Xpay: React.FC<Props> = ({ deposit, previousDeposit, resultInfo, trhash, transactionId }) => {
  const locale = useSelector(select.locale.get);
  const websiteLanguages = useSelector(select.cms.getWebsiteLanguages);
  const [resultDDCStatus, setResultDDCStatus] = useState<DDCStatus>();
  const { jwt } = (resultInfo as XPay3DS2Info) || {};
  const timeout = 600000;
  const xpayTestStatus = useFeature(FeatureFlag.SwcXpayLibraryTestStatus);
  const decodedJwtPayload = !!jwt && jwtDecode<XPAYV3JwtPayload>(jwt);
  const hasV3flag = decodedJwtPayload && decodedJwtPayload?.payload?.requesttypedescriptions;

  const getDDCForm = () => (
    <>
      <div id="st-notification-frame" />
      <form
        id="st-form"
        method="GET"
        action={`${window.location.origin + window.location.pathname}/result`}
      >
        <input type="hidden" name="transactionId" value={transactionId ?? ''} />
        <input type="hidden" name="securityKey" value={trhash ?? ''} />
        {locale !== websiteLanguages[0] && <input type="hidden" name="language" value={locale} />}
        {Object.entries(sanitizeDeposit(deposit)).map(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number')
            return (
              <input key={`deposit.${key}`} type="hidden" name={`deposit.${key}`} value={value} />
            );
          return null;
        })}

        {previousDeposit &&
          Object.entries(sanitizeDeposit(previousDeposit)).map(([key, value]) => {
            if (typeof value === 'string' || typeof value === 'number')
              return (
                <input
                  key={`previousDeposit.${key}`}
                  type="hidden"
                  name={`previousDeposit.${key}`}
                  value={value}
                />
              );
            return null;
          })}
      </form>
    </>
  );

  const appendScripts = useCallback(() => {
    const secureTradingScript: HTMLScriptElement = document.createElement('script');
    const secureTradingInit: HTMLScriptElement = document.createElement('script');

    const srcV2 = 'https://webservices.securetrading.net/js/v2/st.js';
    const srcV3 = 'https://cdn.eu.trustpayments.com/js/latest/st.js';

    secureTradingScript.src = hasV3flag ? srcV3 : srcV2;
    secureTradingScript.onload = () => {
      const innerHTMLv2 = `(function(){var st=SecureTrading({jwt:"${jwt}",
        submitOnError:true, livestatus:${xpayTestStatus ? 0 : 1}});
        st.Components({"requestTypes":["THREEDQUERY","AUTH"], startOnLoad:true});})();`;

      const innerHTMLv3 = `(function(){var st=SecureTrading({jwt:"${jwt}",
        submitOnError:true, livestatus:${xpayTestStatus ? 0 : 1}});
        st.Components({startOnLoad:true});})();`;

      secureTradingInit.innerHTML = hasV3flag ? innerHTMLv3 : innerHTMLv2;
      document.body.appendChild(secureTradingInit);
    };
    document.body.appendChild(secureTradingScript);
  }, [jwt, xpayTestStatus, hasV3flag]);

  useEffect(() => {
    if (!resultInfo || !jwt) {
      setResultDDCStatus(DDCStatus.ddcError);
    } else {
      const timer = setTimeout(() => setResultDDCStatus(DDCStatus.ddcTimeout), timeout);
      setResultDDCStatus(DDCStatus.ddcOk);

      return () => clearTimeout(timer);
    }
  }, [jwt, resultInfo]);

  useEffect(() => {
    if (isBoolean(xpayTestStatus)) {
      appendScripts();
    }
  }, [appendScripts, xpayTestStatus]);

  return (
    <>
      <DDCStatusMessages ddcStatus={resultDDCStatus} />
      {resultDDCStatus === DDCStatus.ddcOk &&
        isBoolean(xpayTestStatus) &&
        // eslint-disable-next-line stars-web/no-direct-browser-globals-access
        createPortal(getDDCForm(), document.body)}
    </>
  );
};

export default Xpay;
